import { useState } from "react";
import {
  Text,
  Group,
  Stack,
  Paper,
  Box,
  Popover,
  ScrollArea,
  Pill,
  PillsInput,
} from "@mantine/core";
import {
  IconGitBranch,
  IconChevronUp,
  IconChevronDown,
  IconPlus,
  IconX,
  IconSearch,
} from "@tabler/icons-react";
import {
  BadgeNotification,
  Button,
  Checkbox,
  DatePicker,
  Input,
  TimePicker,
} from "@onengine/component-library";
import { useFormStore, generateId } from "@/store/form-builder";
import type { QuestionScope } from "@/store/form-builder";
import type {
  FormQuestion,
  ConditionalLogic,
  ConditionType,
  ActionType,
} from "@/components/features/FormBuilder/types/form.types";
import { getConditionsForType, ACTION_OPTIONS, GROUP_ALLOWED_ACTIONS } from "../../data/fieldTypes";
import { BUILDER_LABELS, optionLabel } from "../../data/builderLabels";
import {
  CONTEXT_TRIGGERS,
  CONTEXT_TRIGGER_CONDITIONS,
  getContextTrigger,
} from "../../data/contextTriggers";
import styles from "./ConditionalLogicEditor.module.scss";
import InputDropdown from "@/components/ui/Input/InputDropdown";

type TargetType = "questions" | "sections" | "pages" | "jump_page";
function getTargetType(action: string): TargetType {
  if (["show", "hide", "require"].includes(action)) return "questions";
  if (["show_section", "hide_section"].includes(action)) return "sections";
  if (["show_page", "hide_page"].includes(action)) return "pages";
  return action === "jump_to_page" ? "jump_page" : "questions";
}

interface Props {
  question: FormQuestion;
  scope: QuestionScope;
}

export function ConditionalLogicEditor({ question: q, scope }: Props) {
  const isGroupChild = !!scope.groupId;
  const updateQuestion = useFormStore((s) => s.updateQuestion);
  const getAllQuestions = useFormStore((s) => s.getAllQuestions);
  const getScopeQuestions = useFormStore((s) => s.getScopeQuestions);
  const getExcludedTargetIds = useFormStore((s) => s.getExcludedTargetIds);
  const pages = useFormStore((s) => s.pages);
  const logic = q.conditionalLogic || [];
  const [expanded, setExpanded] = useState(true);
  const excludedIds = getExcludedTargetIds(q.id, isGroupChild ? scope : undefined);
  const others = (isGroupChild ? getScopeQuestions(scope) : getAllQuestions()).filter(
    (x) => x.id !== q.id,
  );
  const conditions = getConditionsForType(q.type);
  const actionOptions = isGroupChild
    ? ACTION_OPTIONS.filter((a) => (GROUP_ALLOWED_ACTIONS as string[]).includes(a.value))
    : ACTION_OPTIONS;
  const isChoice = ["radio", "checkbox", "dropdown"].includes(q.type);
  const isDate = q.type === "date";
  const isTime = q.type === "time";
  const isDateTime = q.type === "date-time";
  const qOpts = q.options || [];

  const addRule = () => {
    updateQuestion(scope, q.id, {
      conditionalLogic: [
        ...logic,
        {
          id: generateId("cl"),
          condition: (conditions[0]?.value || "is") as ConditionType,
          triggerQuestionId: q.id,
          triggerValues: [],
          action: "show" as ActionType,
          targetQuestionIds: [],
          targetSectionIds: [],
          targetPageIds: [],
        },
      ],
      conditionalLogicEnabled: true,
    });
  };
  const updRule = (rid: string, u: Partial<ConditionalLogic>) =>
    updateQuestion(scope, q.id, {
      conditionalLogic: logic.map((r) => (r.id === rid ? { ...r, ...u } : r)),
    });
  const delRule = (rid: string) => {
    const nl = logic.filter((r) => r.id !== rid);
    updateQuestion(scope, q.id, {
      conditionalLogic: nl,
      conditionalLogicEnabled: nl.length > 0,
    });
  };
  const handleActionChange = (rid: string, newAction: string, rule: ConditionalLogic) => {
    const oldT = getTargetType(rule.action),
      newT = getTargetType(newAction);
    const u: Partial<ConditionalLogic> = { action: newAction as ActionType };
    if (oldT !== newT) {
      u.targetQuestionIds = [];
      u.targetSectionIds = [];
      u.targetPageIds = [];
    }
    updRule(rid, u);
  };
  const handleTriggerChange = (rule: ConditionalLogic, triggerId: string) => {
    if (getContextTrigger(triggerId)) {
      updRule(rule.id, { triggerQuestionId: triggerId, condition: "is_any_of", triggerValues: [] });
      return;
    }
    updRule(rule.id, {
      triggerQuestionId: q.id,
      condition: (conditions[0]?.value || "is") as ConditionType,
      triggerValues: [],
      targetQuestionIds: (rule.targetQuestionIds || []).filter((id) => id !== q.id),
    });
  };
  const triggerItems = [
    { value: q.id, label: "This answer" },
    ...CONTEXT_TRIGGERS.map((trigger) => ({ value: trigger.id, label: trigger.label })),
  ];

  const locMap: Record<string, string> = {};
  pages.forEach((p) =>
    p.sections.forEach((s) =>
      s.questions.forEach((qq) => {
        locMap[qq.id] = `${p.name} > ${s.name}`;
      }),
    ),
  );
  const questionTargetItems = others.map((o) => ({
    value: o.id,
    label: o.question || o.typeName || "Untitled",
    description: locMap[o.id],
  }));
  const selfTargetItem = { value: q.id, label: "This question", description: locMap[q.id] };
  const allSections = pages.flatMap((p) =>
    p.sections.map((s) => ({
      id: s.id,
      label: pages.length > 1 ? `${p.name} > ${s.name}` : s.name,
    })),
  );

  return (
    <Paper
      bg="#f8f8fa"
      radius="0 0 md md"
      mt="sm"
      mx={-16}
      mb={-14}
      py="sm"
      px="xl"
      style={{ borderTop: "1px solid #e2e6eb", width: "100%" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Group gap={8} style={{ cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
        <Box className={styles.logicContainer}>
          <IconGitBranch size={15} className={styles.logicIcon} />
          <Text size="sm" fw={500} c="dimmed" className={styles.conditionalLogicText}>
            Conditional Logic
          </Text>
        </Box>

        {logic.length > 0 && (
          <BadgeNotification color="info" size="small">
            {logic.length}
          </BadgeNotification>
        )}
        <Box ml="auto">
          {expanded ? (
            <IconChevronUp size={15} color="#646d7e" />
          ) : (
            <IconChevronDown size={15} color="#646d7e" />
          )}
        </Box>
      </Group>

      {expanded && (
        <Stack gap="sm" mt="sm" pl={22}>
          {logic.map((rule) => {
            const showVal = !["is_empty", "is_not_empty"].includes(rule.condition);
            const isBetween = rule.condition === "is_between";
            const isMultiValue = ["is_any_of", "is_not_any_of"].includes(rule.condition);
            const tType = getTargetType(rule.action);
            const contextTrigger = getContextTrigger(rule.triggerQuestionId);
            const contextValueItems = contextTrigger?.options ?? [];

            return (
              <Paper key={rule.id} p="sm" radius="md" withBorder style={{ borderColor: "#e2e6eb" }}>
                {/* WHEN */}
                <Group gap={8} mb={8} wrap="wrap">
                  <Text
                    id={`${rule.id}-trigger-label`}
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    lts="0.04em"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    When
                  </Text>
                  <InputDropdown
                    value={contextTrigger ? contextTrigger.id : q.id}
                    onChange={(v) => v && handleTriggerChange(rule, v)}
                    items={triggerItems}
                    size="small"
                    labelledBy={`${rule.id}-trigger-label`}
                    className={styles.triggerDropdown}
                  />
                  <InputDropdown
                    value={rule.condition}
                    onChange={(v) =>
                      v && updRule(rule.id, { condition: v as ConditionType, triggerValues: [] })
                    }
                    items={contextTrigger ? CONTEXT_TRIGGER_CONDITIONS : conditions}
                    size="small"
                    style={{ width: 160 }}
                  />
                  {showVal &&
                    (contextTrigger ? (
                      isMultiValue ? (
                        <InputDropdown
                          mode="multi"
                          value={rule.triggerValues || []}
                          onChange={(vals) => updRule(rule.id, { triggerValues: vals })}
                          items={contextValueItems}
                          placeholder="Select values"
                          size="small"
                          style={{ flex: 1, minWidth: 120 }}
                        />
                      ) : (
                        <InputDropdown
                          value={rule.triggerValues?.[0] || null}
                          onChange={(v) => updRule(rule.id, { triggerValues: v ? [v] : [] })}
                          items={contextValueItems}
                          placeholder="Select value"
                          size="small"
                          style={{ flex: 1, minWidth: 120 }}
                        />
                      )
                    ) : isChoice ? (
                      isMultiValue ? (
                        <InputDropdown
                          mode="multi"
                          value={rule.triggerValues || []}
                          onChange={(vals) => updRule(rule.id, { triggerValues: vals })}
                          items={qOpts.map((o) => ({ value: o.id, label: optionLabel(o.value) }))}
                          placeholder="Select values"
                          size="small"
                          style={{ flex: 1, minWidth: 120 }}
                        />
                      ) : (
                        <InputDropdown
                          value={rule.triggerValues?.[0] || null}
                          onChange={(v) => updRule(rule.id, { triggerValues: v ? [v] : [] })}
                          items={qOpts.map((o) => ({ value: o.id, label: optionLabel(o.value) }))}
                          placeholder="Select value"
                          size="small"
                          style={{ flex: 1, minWidth: 120 }}
                        />
                      )
                    ) : isDate ? (
                      <Box style={{ flex: 1, minWidth: 140 }}>
                        <DatePicker
                          value={rule.triggerValues?.[0] ? new Date(rule.triggerValues[0]) : null}
                          onChange={(d) =>
                            updRule(rule.id, {
                              triggerValues: [
                                d ? new Date(d).toISOString().split("T")[0] : "",
                                ...(rule.triggerValues?.slice(1) || []),
                              ],
                            })
                          }
                          placeholder="MM / DD / YYYY"
                        />
                      </Box>
                    ) : isTime ? (
                      <Box style={{ flex: 1, minWidth: 120 }}>
                        <TimePicker
                          value={rule.triggerValues?.[0] || ""}
                          onChange={(v) => updRule(rule.id, { triggerValues: [v] })}
                          placeholder="HH : MM AM"
                        />
                      </Box>
                    ) : isDateTime ? (
                      <Box style={{ flex: 1, minWidth: 180 }}>
                        <DatePicker
                          value={rule.triggerValues?.[0] ? new Date(rule.triggerValues[0]) : null}
                          onChange={(d) =>
                            updRule(rule.id, {
                              triggerValues: [
                                d ? new Date(d).toISOString() : "",
                                ...(rule.triggerValues?.slice(1) || []),
                              ],
                            })
                          }
                          placeholder="MM / DD / YYYY HH:MM"
                        />
                      </Box>
                    ) : (
                      <Box style={{ flex: 1, minWidth: 100 }}>
                        <Input
                          value={rule.triggerValues?.[0] || ""}
                          onChange={(val) => updRule(rule.id, { triggerValues: [val] })}
                          placeholder="Enter value..."
                          size="small"
                        />
                      </Box>
                    ))}
                  {showVal && isBetween && isDate && (
                    <>
                      <Text size="xs" c="dimmed">
                        and
                      </Text>
                      <Box style={{ flex: 1, minWidth: 140 }}>
                        <DatePicker
                          value={rule.triggerValues?.[1] ? new Date(rule.triggerValues[1]) : null}
                          onChange={(d) =>
                            updRule(rule.id, {
                              triggerValues: [
                                rule.triggerValues?.[0] || "",
                                d ? new Date(d).toISOString().split("T")[0] : "",
                              ],
                            })
                          }
                        />
                      </Box>
                    </>
                  )}
                </Group>
                {/* THEN */}
                <Group gap={8} wrap="wrap">
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" lts="0.04em">
                    Then
                  </Text>
                  <InputDropdown
                    value={rule.action}
                    onChange={(v) => v && handleActionChange(rule.id, v, rule)}
                    items={actionOptions}
                    size="small"
                    style={{ width: 160 }}
                  />
                  {tType === "questions" && (
                    <MultiTargetPopover
                      selected={rule.targetQuestionIds || []}
                      placeholder="Select questions"
                      items={
                        contextTrigger
                          ? [selfTargetItem, ...questionTargetItems]
                          : questionTargetItems
                      }
                      disabledIds={contextTrigger ? new Set<string>() : excludedIds}
                      onChange={(ids) => updRule(rule.id, { targetQuestionIds: ids })}
                    />
                  )}
                  {tType === "sections" && (
                    <MultiTargetPopover
                      selected={rule.targetSectionIds || []}
                      placeholder="Select sections"
                      items={allSections.map((s) => ({ value: s.id, label: s.label }))}
                      onChange={(ids) => updRule(rule.id, { targetSectionIds: ids })}
                    />
                  )}
                  {tType === "pages" && (
                    <MultiTargetPopover
                      selected={rule.targetPageIds || []}
                      placeholder="Select pages"
                      items={pages.map((p) => ({ value: p.id, label: p.name }))}
                      onChange={(ids) => updRule(rule.id, { targetPageIds: ids })}
                    />
                  )}
                  {tType === "jump_page" && (
                    <InputDropdown
                      value={rule.targetPageIds?.[0] || null}
                      onChange={(v) => updRule(rule.id, { targetPageIds: v ? [v] : [] })}
                      items={pages.map((p) => ({ value: p.id, label: p.name }))}
                      placeholder="Select page"
                      size="small"
                      style={{ flex: 1, minWidth: 140 }}
                    />
                  )}
                  <Button
                    type="icon"
                    variant="destructive-secondary"
                    size="small"
                    iconLeft={<IconX size={16} />}
                    htmlAttributes={{ "aria-label": BUILDER_LABELS.removeRule }}
                    onClick={() => delRule(rule.id)}
                    style={{ border: "none" }}
                  />
                </Group>
              </Paper>
            );
          })}
          <Text
            component="button"
            size="sm"
            fw={600}
            c="#004ddb"
            onClick={addRule}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 0",
            }}
          >
            <IconPlus size={14} /> Add condition
          </Text>
        </Stack>
      )}
    </Paper>
  );
}

function MultiTargetPopover({
  selected,
  items,
  placeholder,
  onChange,
  disabledIds,
}: {
  selected: string[];
  items: { value: string; label: string; description?: string }[];
  placeholder: string;
  onChange: (ids: string[]) => void;
  disabledIds?: Set<string>;
}) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = items.filter(
    (i) =>
      !search ||
      i.label.toLowerCase().includes(search.toLowerCase()) ||
      (i.description || "").toLowerCase().includes(search.toLowerCase()),
  );

  const handleToggle = (value: string) => {
    const isSelected = selected.includes(value);
    if (disabledIds?.has(value) && !isSelected) return;
    onChange(isSelected ? selected.filter((x) => x !== value) : [...selected, value]);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="lg"
      width={320}
      withArrow
    >
      <Popover.Target>
        <PillsInput
          size="xs"
          radius="md"
          style={{ flex: 1, minWidth: 160, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setOpened(!opened);
          }}
          rightSection={
            <IconChevronDown
              size={14}
              color="#646d7e"
              style={{
                transform: opened ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            />
          }
        >
          <Pill.Group>
            {selected.length === 0 && (
              <Text size="xs" c="dimmed" py={2}>
                {placeholder}
              </Text>
            )}
            {selected.map((id) => {
              const item = items.find((i) => i.value === id);
              if (!item) return null;
              return (
                <Pill
                  key={id}
                  withRemoveButton
                  onRemove={() => onChange(selected.filter((x) => x !== id))}
                  size="xs"
                >
                  {item.description ? `${item.label} (${item.description})` : item.label}
                </Pill>
              );
            })}
          </Pill.Group>
        </PillsInput>
      </Popover.Target>
      <Popover.Dropdown p={0} onClick={(e) => e.stopPropagation()}>
        <Box p="xs" style={{ borderBottom: "1px solid var(--neutral-300,#f0f5ff)" }}>
          <Input
            value={search}
            onChange={setSearch}
            placeholder="Search..."
            size="small"
            iconLeft={<IconSearch size={14} />}
          />
        </Box>
        <ScrollArea mah={200} p="xs" style={{ overflow: "auto" }}>
          {filtered.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="sm">
              No items found
            </Text>
          )}
          {disabledIds ? (
            <Stack gap={4}>
              {filtered.map((item) => {
                const isDisabled = disabledIds.has(item.value);
                const isChecked = selected.includes(item.value);
                const isBlockedDisabled = isDisabled && !isChecked;
                return (
                  <Group
                    key={item.value}
                    gap={8}
                    py={4}
                    px={4}
                    style={{
                      cursor: isBlockedDisabled ? "not-allowed" : "pointer",
                      opacity: isBlockedDisabled ? 0.45 : 1,
                      borderRadius: 6,
                      background: isChecked ? "var(--neutral-300,#f0f5ff)" : "transparent",
                    }}
                    onClick={() => handleToggle(item.value)}
                    wrap="nowrap"
                  >
                    <Box
                      w={16}
                      h={16}
                      style={{
                        borderRadius: 4,
                        border: `2px solid ${isChecked ? "#004ddb" : "#cfd4dd"}`,
                        background: isChecked ? "#004ddb" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isChecked && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path
                            d="M2 5L4 7L8 3"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" c={isDisabled ? "dimmed" : "#363d4d"} truncate>
                        {item.description ? `${item.label} (${item.description})` : item.label}
                      </Text>
                      {isDisabled && (
                        <Text size="xs" c="dimmed" fs="italic">
                          Cannot select (circular logic)
                        </Text>
                      )}
                    </Box>
                  </Group>
                );
              })}
            </Stack>
          ) : (
            <Checkbox
              options={filtered.map((item) => ({
                label: item.description ? `${item.label} (${item.description})` : item.label,
                value: item.value,
              }))}
              selectedValues={selected}
              onValuesChange={onChange}
              size="small"
            />
          )}
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}
